"use client";

import { cn } from "@newxsystems/shared";
import { Icon } from "@templates/design-system";
import { useId, useState } from "react";
import { AbyssRichText } from "../AbyssRichText/AbyssRichText";
import type { IAccordionListProps } from "./AccordionList.props";

export const AccordionList = ({ items, openBehavior }: IAccordionListProps) => {
  const firstOpen = items.findIndex((item) => item.initiallyOpen);
  const [openItems, setOpenItems] = useState<number[]>(
    openBehavior === "multiple-open"
      ? items.flatMap((item, index) => (item.initiallyOpen ? [index] : []))
      : firstOpen >= 0
        ? [firstOpen]
        : [],
  );
  const baseId = useId();
  const toggle = (index: number) =>
    setOpenItems((current) =>
      current.includes(index)
        ? current.filter((item) => item !== index)
        : openBehavior === "multiple-open"
          ? [...current, index]
          : [index],
    );

  return (
    <div className="border-t border-iron">
      {items.map((item, index) => {
        const open = openItems.includes(index);
        const triggerId = `${baseId}-trigger-${index}`;
        const panelId = `${baseId}-panel-${index}`;
        return (
          <div className={cn("border-b border-iron", open && "bg-obsidian/45")} key={index}>
            <h3>
              <button
                aria-controls={panelId}
                aria-expanded={open}
                className={cn(
                  "flex min-h-16 w-full cursor-pointer items-center justify-between gap-5 px-4 py-5 text-left font-abyss-display text-xl font-medium transition-colors hover:text-primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring",
                  open && "text-cyan-trace",
                )}
                id={triggerId}
                onClick={() => toggle(index)}
                type="button"
              >
                <span>{item.title}</span>
                <Icon
                  className={cn(
                    "size-5 shrink-0 text-cyan-trace transition-transform duration-200",
                    open && "rotate-45",
                  )}
                  name="plus"
                />
              </button>
            </h3>
            <div
              aria-hidden={!open}
              aria-labelledby={triggerId}
              className={cn(
                "grid transition-[grid-template-rows] duration-300 ease-out",
                open ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
              )}
              id={panelId}
              inert={!open}
              role="region"
            >
              <div className="overflow-hidden">
                <AbyssRichText
                  className="max-w-[65ch] px-4 pb-6 text-base text-fog md:text-lg"
                  content={item.content}
                />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

AccordionList.displayName = "Content::AccordionList";
